import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import logo from "../../../assets/images/wa-horizontal-color.png";
import "./pagar.css";

/*
	"Reporta tu pago" (2026-10-10): reemplaza los Zoho Forms "Posteo de Pagos (estudiantes)" que se
	apagan con Zoho. Pide lo mismo que el formulario: nombre, teléfono, correo, motivo y comprobantes
	(Online además el país). Sin sesión de WAPP. El ticket cae en Pagos por Procesar del plantel y Caja
	lo aplica como siempre.
*/

interface Plantel { slug: string; nombre: string; pais: string; telefono: string; inicial: string }
export const PLANTELES: Plantel[] = [
	{ slug: "sps", nombre: "San Pedro Sula", pais: "Honduras", telefono: "9999 9999", inicial: "SPS" },
	{ slug: "tgu", nombre: "Tegucigalpa", pais: "Honduras", telefono: "9999 9999", inicial: "TGU" },
	{ slug: "mty", nombre: "Monterrey", pais: "México", telefono: "81 1234 5678", inicial: "MTY" },
	{ slug: "trc", nombre: "Torreón", pais: "México", telefono: "871 123 4567", inicial: "TRC" },
	{ slug: "online", nombre: "Online", pais: "Clases en línea", telefono: "Con lada de tu país", inicial: "ON" }
];
const PAISES = ["Honduras", "México", "Otro"];
const MAX_ARCHIVOS = 5;
const MAX_BYTES = 10 * 1024 * 1024;
const API = (process.env.REACT_APP_API_URL_V2 || "") + "/pagar/";

interface Archivo { id: string; file: File; vista: string | null }

const peso = (b: number) => b >= 1024 * 1024 ? (b / 1024 / 1024).toFixed(1) + " MB" : Math.max(1, Math.round(b / 1024)) + " KB";
const esValido = (f: File) => /^image\//.test(f.type) || f.type === "application/pdf" || /\.(jpe?g|png|webp|heic|heif|pdf)$/i.test(f.name);

const Cabeza: React.FC<{ plantel?: Plantel }> = ({ plantel }) => (
	<header className="pgCabeza">
		<img src={logo} alt="Washington Academy" className="pgLogo" />
		{plantel ? <span className="pgPlantel">{plantel.nombre}<a href="/pagar">Cambiar</a></span> : null}
	</header>
);

const ElegirPlantel: React.FC = () => (
	<div className="pgCuerpo pgEntra">
		<h1 className="pgTitulo">Reporta Tu Pago</h1>
		<p className="pgBajada">¿En qué plantel estudias?</p>
		<div className="pgPlanteles">
			{PLANTELES.map((p) => (
				<a key={p.slug} className="pgOpcion" href={"/pagar/" + p.slug}>
					<span className="pgInicial">{p.inicial}</span>
					<span className="pgOpcionTexto"><b>{p.nombre}</b><small>{p.pais}</small></span>
					<FontAwesomeIcon className="pgFlecha" icon={solid("chevron-right")} />
				</a>
			))}
		</div>
	</div>
);

const Formulario: React.FC<{ plantel: Plantel }> = ({ plantel }) => {
	const [nombre, setNombre] = useState("");
	const [telefono, setTelefono] = useState("");
	const [correo, setCorreo] = useState("");
	const [pais, setPais] = useState("");
	const [motivo, setMotivo] = useState("");
	const [trampa, setTrampa] = useState("");
	const [archivos, setArchivos] = useState<Archivo[]>([]);
	const [aviso, setAviso] = useState<string | null>(null);
	const [enviando, setEnviando] = useState(false);
	const [avance, setAvance] = useState(0);
	const [folio, setFolio] = useState<number | null>(null);
	const entrada = useRef<HTMLInputElement>(null);
	const caja = useRef<HTMLDivElement>(null);
	const online = plantel.slug === "online";
	const correoObligatorio = plantel.slug === "trc";

	useEffect(() => () => { archivos.forEach((a) => a.vista && URL.revokeObjectURL(a.vista)); }, []); // eslint-disable-line react-hooks/exhaustive-deps

	const avisar = (t: string) => {
		setAviso(t);
		caja.current?.closest(".pg")?.scrollTo({ top: 0, behavior: "smooth" });
	};

	const agregar = (lista: FileList | null) => {
		if (!lista) return;
		const nuevos: Archivo[] = [];
		for (const f of Array.from(lista)) {
			if (!esValido(f)) { avisar("El comprobante tiene que ser foto o PDF."); continue; }
			if (f.size > MAX_BYTES) { avisar(f.name + " pesa más de 10 MB."); continue; }
			nuevos.push({ id: Math.random().toString(36).slice(2), file: f, vista: /^image\/(jpe?g|png|webp|gif)$/.test(f.type) ? URL.createObjectURL(f) : null });
		}
		setArchivos((a) => {
			const todo = [...a, ...nuevos];
			if (todo.length > MAX_ARCHIVOS) setAviso("Puedes subir hasta " + MAX_ARCHIVOS + " comprobantes.");
			return todo.slice(0, MAX_ARCHIVOS);
		});
		if (entrada.current) entrada.current.value = "";
	};

	const quitar = (id: string) => setArchivos((a) => {
		const x = a.find((y) => y.id === id);
		if (x?.vista) URL.revokeObjectURL(x.vista);
		return a.filter((y) => y.id !== id);
	});

	const enviar = async (e: React.FormEvent) => {
		e.preventDefault();
		setAviso(null);
		const digitos = telefono.replace(/\D/g, "");
		if (nombre.trim().length < 3) return avisar("Escribe tu nombre completo.");
		if (digitos.length < 8) return avisar("Escribe tu número de teléfono completo.");
		if (correoObligatorio && correo.trim() === "") return avisar("Escribe tu correo.");
		if (correo.trim() !== "" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(correo.trim())) return avisar("Revisa tu correo.");
		if (online && !pais) return avisar("Elige tu país.");
		if (archivos.length < 1) return avisar("Sube la foto o el PDF de tu comprobante.");
		const fd = new FormData();
		fd.append("nombre", nombre.trim());
		fd.append("telefono", telefono.trim());
		fd.append("correo", correo.trim());
		fd.append("motivo", motivo.trim());
		if (online) fd.append("pais", pais);
		fd.append("sitio", trampa);
		archivos.forEach((a) => fd.append("files[]", a.file, a.file.name));
		setEnviando(true);
		setAvance(0);
		try {
			const r = await axios.post<{ folio: number }>(API + plantel.slug, fd, {
				timeout: 180000,
				headers: { Accept: "application/json" },
				onUploadProgress: (p) => p.total ? setAvance(Math.round((p.loaded / p.total) * 100)) : undefined
			});
			setFolio(r.data.folio);
			caja.current?.closest(".pg")?.scrollTo({ top: 0 });
		} catch (err: any) {
			const st = err?.response?.status;
			avisar(st === 429 ? "Ya enviaste varios reportes seguidos. Espera un minuto e intenta otra vez."
				: err?.response?.data?.message || (err?.response ? "No pudimos recibir tu pago. Intenta otra vez." : "No hay conexión. Revisa tu internet e intenta otra vez."));
		} finally {
			setEnviando(false);
		}
	};

	const otroPago = () => {
		archivos.forEach((a) => a.vista && URL.revokeObjectURL(a.vista));
		setArchivos([]);
		setMotivo("");
		setFolio(null);
		setAviso(null);
	};

	if (folio !== null) {
		return (
			<div className="pgCuerpo pgListo pgEntra" ref={caja}>
				<div className="pgPalomita"><FontAwesomeIcon icon={solid("check")} /></div>
				<h1 className="pgTitulo">¡Listo!</h1>
				<p className="pgBajada">Recibimos tu pago, Caja lo va a revisar.</p>
				{folio > 0 ? <div className="pgFolio"><span>Tu folio</span><b>#{folio}</b></div> : null}
				<button type="button" className="pgBoton secundario" onClick={otroPago}><span>Enviar Otro Pago</span></button>
			</div>
		);
	}

	return (
		<div className="pgCuerpo pgEntra" ref={caja}>
			<h1 className="pgTitulo">Reporta Tu Pago</h1>
			<p className="pgBajada">Sube tu comprobante y Caja lo aplica.</p>
			{aviso ? <div className="pgAviso" role="alert"><FontAwesomeIcon icon={solid("circle-exclamation")} /><span>{aviso}</span></div> : null}
			<form onSubmit={enviar} noValidate>
				<div className="pgTarjeta">
					<div className="pgCampo">
						<label className="pgEtiqueta" htmlFor="pg-nombre">Nombre completo</label>
						<input id="pg-nombre" className="pgInput" value={nombre} onChange={(e) => setNombre(e.target.value)} autoComplete="name" maxLength={150} placeholder="Como aparece en tu matrícula" />
					</div>
					<div className="pgCampo">
						<label className="pgEtiqueta" htmlFor="pg-telefono">Teléfono</label>
						<input id="pg-telefono" className="pgInput" type="tel" inputMode="tel" value={telefono} onChange={(e) => setTelefono(e.target.value)} autoComplete="tel" maxLength={25} placeholder={plantel.telefono} />
					</div>
					<div className="pgCampo">
						<label className="pgEtiqueta" htmlFor="pg-correo">Correo {correoObligatorio ? null : <small>(opcional)</small>}</label>
						<input id="pg-correo" className="pgInput" type="email" inputMode="email" value={correo} onChange={(e) => setCorreo(e.target.value)} autoComplete="email" maxLength={150} placeholder="tu@correo.com" />
					</div>
					{online ? (
						<div className="pgCampo">
							<span className="pgEtiqueta">País</span>
							<div className="pgPaises" role="radiogroup" aria-label="País">
								{PAISES.map((p) => (
									<button key={p} type="button" role="radio" aria-checked={pais === p} className={"pgPais" + (pais === p ? " activo" : "")} onClick={() => setPais(p)}>{p}</button>
								))}
							</div>
						</div>
					) : null}
					<div className="pgCampo">
						<label className="pgEtiqueta" htmlFor="pg-motivo">Motivo del pago <small>(opcional)</small></label>
						<textarea id="pg-motivo" className="pgInput" rows={2} value={motivo} onChange={(e) => setMotivo(e.target.value)} maxLength={500} placeholder="Ej. Mensualidad de octubre" />
					</div>
					<div className="pgTrampa" aria-hidden="true">
						<label htmlFor="pg-sitio">Sitio</label>
						<input id="pg-sitio" tabIndex={-1} autoComplete="off" value={trampa} onChange={(e) => setTrampa(e.target.value)} />
					</div>
				</div>

				<div className="pgTarjeta">
					<span className="pgEtiqueta">Comprobante</span>
					{archivos.map((a) => (
						<div key={a.id} className="pgArchivo">
							<div className="pgMiniatura">{a.vista ? <img src={a.vista} alt="" /> : <FontAwesomeIcon icon={solid("file-lines")} />}</div>
							<div className="pgArchivoTexto">
								<div className="pgArchivoNombre">{a.file.name}</div>
								<div className="pgArchivoPeso">{peso(a.file.size)}</div>
							</div>
							<button type="button" className="pgQuitar" aria-label={"Quitar " + a.file.name} disabled={enviando} onClick={() => quitar(a.id)}><FontAwesomeIcon icon={solid("xmark")} /></button>
						</div>
					))}
					{archivos.length < MAX_ARCHIVOS ? (
						<button type="button" className={"pgSubir" + (archivos.length ? " chico" : "")} disabled={enviando} onClick={() => entrada.current?.click()}>
							<FontAwesomeIcon icon={archivos.length ? solid("plus") : solid("camera")} />
							{archivos.length ? "Agregar Otro" : "Subir Comprobante"}
							{archivos.length ? null : <small>Foto o PDF</small>}
						</button>
					) : null}
					<input ref={entrada} type="file" accept="image/*,application/pdf" multiple hidden onChange={(e) => agregar(e.target.files)} />
				</div>

				<button type="submit" className="pgBoton" disabled={enviando}>
					{enviando ? <><span className="pgAvance" style={{ width: avance + "%" }} /><span className="pgGiro" /><span>{avance < 100 ? "Enviando… " + avance + "%" : "Enviando…"}</span></> : <span>Enviar</span>}
				</button>
			</form>
		</div>
	);
};

/** Página pública /pagar y /pagar/<plantel>. */
const Pagar: React.FC<{ slug?: string }> = ({ slug }) => {
	const plantel = PLANTELES.find((p) => p.slug === (slug || "").toLowerCase());
	useEffect(() => {
		// Sin la app alrededor nadie pone el tema: los colores --wa-* viven en el tema claro.
		document.documentElement.classList.add("theme-mode-light");
		document.title = "Reporta tu pago" + (plantel ? " · " + plantel.nombre : "") + " · Washington Academy";
	}, [plantel]);
	return (
		<div className="pg">
			<Cabeza plantel={plantel} />
			{plantel ? <Formulario plantel={plantel} /> : <ElegirPlantel />}
		</div>
	);
};

export default Pagar;
